/**
 * Intraclass correlation coefficients from a subjects × raters table.
 *
 * Six forms are computed from one two-way ANOVA. They carry two sets of names:
 *
 * | Shrout & Fleiss (1979) | McGraw & Wong (1996)                          | Model                 |
 * | ---------------------- | --------------------------------------------- | --------------------- |
 * | ICC(1,1)               | ICC(1)                                        | one-way random        |
 * | ICC(2,1)               | ICC(A,1)                                      | two-way, absolute     |
 * | ICC(3,1)               | ICC(C,1)                                      | two-way, consistency  |
 * | ICC(1,k)               | ICC(k)                                        | one-way random        |
 * | ICC(2,k)               | ICC(A,k)                                      | two-way, absolute     |
 * | ICC(3,k)               | ICC(C,k)                                      | two-way, consistency  |
 *
 * The point estimates are the same whether the raters are treated as random (Shrout &
 * Fleiss case 2) or fixed (case 3) for the consistency forms; they differ in what the
 * coefficient generalises to, not in the arithmetic.
 *
 * Confidence intervals are the F-based ones: Shrout & Fleiss (1979, Table 4) for the one-way
 * and consistency forms, and the Satterthwaite-approximated degrees of freedom of McGraw &
 * Wong (1996, Table 7) for the absolute-agreement forms. Average-measures bounds are the
 * single-measure bounds carried through the Spearman–Brown step k·ρ / (1 + (k − 1)·ρ).
 */

import { fQuantile, fUpper } from "@/lib/stats/distributions";

export type IccForm = "1,1" | "2,1" | "3,1" | "1,k" | "2,k" | "3,k";

export const ICC_FORMS: readonly IccForm[] = [
  "1,1",
  "2,1",
  "3,1",
  "1,k",
  "2,k",
  "3,k",
];

export interface IccAnova {
  n: number;
  k: number;
  ssRows: number;
  ssColumns: number;
  ssError: number;
  ssWithin: number;
  msRows: number;
  msColumns: number;
  msError: number;
  msWithin: number;
}

export interface IccEstimate {
  form: IccForm;
  estimate: number;
  lower: number;
  upper: number;
  /** The F statistic that tests ICC = 0 and its degrees of freedom. */
  f: number;
  df1: number;
  df2: number;
  p: number;
}

export interface IccResult {
  anova: IccAnova;
  level: number;
  estimates: IccEstimate[];
}

/** Two-way ANOVA sums of squares for a complete subjects × raters table. */
export function iccAnova(rows: readonly (readonly number[])[]): IccAnova {
  const n = rows.length;
  const k = rows[0]?.length ?? 0;
  let grand = 0;
  for (const row of rows) {
    for (const value of row) {
      grand += value;
    }
  }
  const mean = grand / (n * k);
  let ssRows = 0;
  for (const row of rows) {
    const rowMean = row.reduce((sum, value) => sum + value, 0) / k;
    ssRows += k * (rowMean - mean) ** 2;
  }
  let ssColumns = 0;
  for (let j = 0; j < k; j++) {
    let total = 0;
    for (const row of rows) {
      total += row[j];
    }
    ssColumns += n * (total / n - mean) ** 2;
  }
  let ssTotal = 0;
  for (const row of rows) {
    for (const value of row) {
      ssTotal += (value - mean) ** 2;
    }
  }
  const ssError = ssTotal - ssRows - ssColumns;
  const ssWithin = ssColumns + ssError;
  return {
    n,
    k,
    ssRows,
    ssColumns,
    ssError,
    ssWithin,
    msRows: ssRows / (n - 1),
    msColumns: ssColumns / (k - 1),
    msError: ssError / ((n - 1) * (k - 1)),
    msWithin: ssWithin / (n * (k - 1)),
  };
}

/** The single-measure point estimate for the three models. */
function singleEstimate(a: IccAnova, model: 1 | 2 | 3): number {
  const { n, k, msRows, msColumns, msError, msWithin } = a;
  if (model === 1) {
    return (msRows - msWithin) / (msRows + (k - 1) * msWithin);
  }
  if (model === 2) {
    return (
      (msRows - msError) /
      (msRows + (k - 1) * msError + (k * (msColumns - msError)) / n)
    );
  }
  return (msRows - msError) / (msRows + (k - 1) * msError);
}

/** The average-measures estimate for the three models. */
function averageEstimate(a: IccAnova, model: 1 | 2 | 3): number {
  const { n, msRows, msColumns, msError, msWithin } = a;
  if (model === 1) {
    return (msRows - msWithin) / msRows;
  }
  if (model === 2) {
    return (msRows - msError) / (msRows + (msColumns - msError) / n);
  }
  return (msRows - msError) / msRows;
}

/** F-based bounds for ICC(1,1) and ICC(3,1) (Shrout & Fleiss 1979). */
function simpleBounds(
  a: IccAnova,
  model: 1 | 3,
  level: number
): { lower: number; upper: number } {
  const { n, k, msRows, msError, msWithin } = a;
  const df2 = model === 1 ? n * (k - 1) : (n - 1) * (k - 1);
  const f = msRows / (model === 1 ? msWithin : msError);
  const tail = fQuantile(1 - (1 - level) / 2, n - 1, df2);
  const tailSwapped = fQuantile(1 - (1 - level) / 2, df2, n - 1);
  const fLower = f / tail;
  const fUpperBound = f * tailSwapped;
  return {
    lower: (fLower - 1) / (fLower + k - 1),
    upper: (fUpperBound - 1) / (fUpperBound + k - 1),
  };
}

/** Bounds for ICC(2,1) with the Satterthwaite degrees of freedom of McGraw & Wong. */
function absoluteBounds(
  a: IccAnova,
  rho: number,
  level: number
): { lower: number; upper: number } {
  const { n, k, msRows, msColumns, msError } = a;
  const fColumns = msColumns / msError;
  const first = k * rho;
  const second = n * (1 + (k - 1) * rho) - k * rho;
  const nu =
    (first * fColumns + second) ** 2 /
    ((first * fColumns) ** 2 / (k - 1) + second ** 2 / ((n - 1) * (k - 1)));
  const tailProbability = 1 - (1 - level) / 2;
  const fHigh = fQuantile(tailProbability, n - 1, nu);
  const fLow = fQuantile(tailProbability, nu, n - 1);
  const shared = k * msColumns + (k * n - k - n) * msError;
  return {
    lower: (n * (msRows - fHigh * msError)) / (fHigh * shared + n * msRows),
    upper: (n * (fLow * msRows - msError)) / (shared + n * fLow * msRows),
  };
}

function spearmanBrown(rho: number, k: number): number {
  return (k * rho) / (1 + (k - 1) * rho);
}

function parseForm(form: IccForm): { model: 1 | 2 | 3; average: boolean } {
  return {
    model: Number(form[0]) as 1 | 2 | 3,
    average: form.endsWith("k"),
  };
}

/** One form with its interval and its test of ICC = 0. */
export function iccEstimate(
  a: IccAnova,
  form: IccForm,
  level = 0.95
): IccEstimate {
  const { model, average } = parseForm(form);
  const { n, k } = a;
  const single = singleEstimate(a, model);
  let bounds: { lower: number; upper: number };
  if (model === 2) {
    bounds = absoluteBounds(a, single, level);
  } else {
    bounds = simpleBounds(a, model, level);
  }
  const df2 = model === 1 ? n * (k - 1) : (n - 1) * (k - 1);
  const f = a.msRows / (model === 1 ? a.msWithin : a.msError);
  const common = { form, f, df1: n - 1, df2, p: fUpper(f, n - 1, df2) };
  if (!average) {
    return { ...common, estimate: single, ...bounds };
  }
  return {
    ...common,
    estimate: averageEstimate(a, model),
    lower: spearmanBrown(bounds.lower, k),
    upper: spearmanBrown(bounds.upper, k),
  };
}

/** All six forms. Needs at least two subjects and two raters, with no missing cells. */
export function intraclassCorrelations(
  rows: readonly (readonly number[])[],
  level = 0.95
): IccResult | null {
  const n = rows.length;
  const k = rows[0]?.length ?? 0;
  if (n < 2 || k < 2) {
    return null;
  }
  const anova = iccAnova(rows);
  if (!(anova.msRows > 0 || anova.msError > 0 || anova.msWithin > 0)) {
    return null;
  }
  return {
    anova,
    level,
    estimates: ICC_FORMS.map((form) => iccEstimate(anova, form, level)),
  };
}

export type IccBand = "poor" | "moderate" | "good" | "excellent";

/**
 * Koo & Li (2016): below 0.5 poor, 0.5–0.75 moderate, 0.75–0.9 good, above 0.9 excellent.
 * They apply it to the 95% confidence interval's bounds rather than to the estimate alone.
 */
export function kooLiBand(value: number): IccBand {
  if (value < 0.5) {
    return "poor";
  }
  if (value < 0.75) {
    return "moderate";
  }
  if (value <= 0.9) {
    return "good";
  }
  return "excellent";
}
